import { CONTACT_GLYPHS, type IconGlyph } from "./icons";
import {
  box,
  col,
  expanded,
  flexible,
  glyph,
  hspace,
  image,
  keep,
  linkText,
  padding,
  rich,
  row,
  rowMin,
  sized,
  text,
  vspace,
  wrapBox,
  type BoxNode,
  type Columns,
  type Edge,
  type Node,
  type PageSize,
  type PageSpec,
  type TextNode,
} from "./ir";
import {
  entryHasHeader,
  hasCategories,
  skillGroups,
  type ContactItem,
  type ContactKind,
  type RenderEntry,
  type RenderResume,
  type RenderSection,
  type RenderSkill,
} from "./model";
import type { FamilyId } from "./fonts";
import { GREY_500, hex, Pal, type RenderTheme, type Rgb, type TextStyle } from "./theme";

// Port of the Nohaya Career app's template_base.dart: the template contract,
// the shared kit (contacts, icons, bullets, chips, level bars, photo, page
// frames) and FlowTemplate, which turns sections into page-safe blocks
// through per-template styling hooks.

export type TemplateCategory = "modern" | "classic" | "creative" | "executive" | "minimal" | "tech";

export type TemplateInfo = {
  id: string;
  name: string;
  tagline: string;
  category: TemplateCategory;
  /** "One column", "Sidebar" … */
  layout: string;
  defaultAccent: number;
  atsFriendly: boolean;
  supportsPhoto: boolean;
};

/**
 * Font families a template uses. When [headingFollowsBody] /
 * [displayFollowsBody] are true (the default), the user's typeface choice
 * replaces those roles too; otherwise they stay part of the template's look.
 */
export type TemplateFonts = {
  body: FamilyId;
  heading?: FamilyId;
  display?: FamilyId;
  usesMono?: boolean;
  headingFollowsBody?: boolean;
  displayFollowsBody?: boolean;
};

/** A4 and US Letter in points. */
export const A4: PageSize = { width: 595.28, height: 841.89 };
export const LETTER: PageSize = { width: 612, height: 792 };

/** How contact (and section) icons are drawn. */
export type IconStyle = "none" | "filled" | "outlined" | "circle" | "square";

export type RenderContext = {
  model: RenderResume;
  t: RenderTheme;
  format: PageSize;
  iconStyle: IconStyle;
  /** Contact icons on (any style but "none"). */
  showIcons: boolean;
  /** The section-icon table when icons beside headings are on, else null. */
  sectionIcons: Record<string, IconGlyph> | null;
  /** Bars/dots for skill and language levels instead of plain lists. */
  showSkillLevels: boolean;
};

export type Zone = "main" | "sidebar";

export type ZoneColors = { text: Rgb; strong: Rgb; muted: Rgb; accent: Rgb; line: Rgb };

// ---- kit ------------------------------------------------------------------

/**
 * Splits long text at sentence (then word) boundaries so no single block can
 * outgrow a page.
 */
export function chunkText(value: string, max = 650): string[] {
  if (value.length <= max) return [value];
  const sentences = value.match(/[^.!?;]+[.!?;]*\s*/g) ?? [value];
  const out: string[] = [];
  let buf = "";
  const flush = () => {
    const s = buf.trim();
    if (s) out.push(s);
    buf = "";
  };
  for (const sentence of sentences) {
    if (sentence.length > max) {
      flush();
      for (const w of sentence.split(" ")) {
        if (buf.length + w.length + 1 > max) flush();
        buf += `${w} `;
      }
      flush();
      continue;
    }
    if (buf.length + sentence.length > max) flush();
    buf += sentence;
  }
  flush();
  return out.length ? out : [value];
}

/**
 * A glyph drawn in an icon style: solid or outline glyph in [color], or a
 * glyph knocked out of a [color] circle / rounded square.
 */
export function styledGlyph(filled: string, outlined: string, style: IconStyle, color: Rgb, size: number): Node {
  switch (style) {
    case "circle":
    case "square": {
      const b = size * 1.3;
      return box(
        { width: b, height: b, bg: hex(color), radius: style === "circle" ? b / 2 : b * 0.24, align: "center", justify: "center" },
        [glyph(filled, Pal.on(color), size * 0.74)]
      );
    }
    case "outlined":
      return glyph(outlined, color, size);
    default:
      return glyph(filled, color, size);
  }
}

/** A small vector contact icon in [color], in [style]. */
export function contactIcon(kind: ContactKind, color: Rgb, size: number, style: IconStyle = "filled"): Node {
  const [filled, outlined] = CONTACT_GLYPHS[kind] ?? CONTACT_GLYPHS.link;
  return styledGlyph(filled, outlined, style, color, size);
}

/**
 * The section's heading icon when section icons are on (null otherwise).
 * Plain solid/outline glyphs follow the icon style; badge styles apply too.
 */
export function sectionIcon(c: RenderContext, s: RenderSection, color: Rgb, size: number, plain = false): Node | null {
  if (!c.sectionIcons) return null;
  const g = c.sectionIcons[s.iconId ?? ""] ?? c.sectionIcons.sparkle;
  if (!g) return null;
  const style: IconStyle =
    plain || c.iconStyle === "none" ? (c.iconStyle === "outlined" ? "outlined" : "filled") : c.iconStyle;
  return styledGlyph(g.filled, g.outlined, style, color, size);
}

/** [label] with [icon] in front of it (just [label] when there's no icon). */
export function leadIcon(icon: Node | null, label: Node, o: { gap?: number; center?: boolean } = {}): Node {
  if (!icon) return label;
  return rowMin([icon, hspace(o.gap ?? 6), flexible(label)], { justify: o.center ? "center" : "start" });
}

/** One contact item: optional icon + (linked) text that may wrap within its column. */
export function contactChip(
  c: RenderContext,
  item: ContactItem,
  o: { style: TextStyle; iconColor?: Rgb; iconSize?: number; gap?: number }
): Node {
  const size = o.iconSize ?? o.style.size * 1.05;
  return rowMin(
    [
      c.showIcons && contactIcon(item.kind, o.iconColor ?? o.style.color, size, c.iconStyle),
      c.showIcons && hspace(o.gap ?? 4),
      flexible(linkText(item.text, o.style, item.url)),
    ],
    { shrinkable: true }
  );
}

/** Contact items flowing on one or more lines. */
export function contactWrap(
  c: RenderContext,
  o: { style: TextStyle; iconColor?: Rgb; spacing?: number; runSpacing?: number; center?: boolean }
): Node {
  return wrapBox(
    c.model.contacts.map((item) => contactChip(c, item, { style: o.style, iconColor: o.iconColor })),
    { spacing: o.spacing ?? 12, runSpacing: o.runSpacing ?? 4, center: o.center }
  );
}

/** Classic "a  |  b  |  c" contact line (never shows icons). */
export function contactLine(c: RenderContext, o: { style: TextStyle; separator?: string }): Node {
  const list = c.model.contacts;
  const sep = o.separator ?? "   |   ";
  return wrapBox(
    list.map((item, i) =>
      rowMin([
        flexible(linkText(item.text, o.style, item.url)),
        i < list.length - 1 && text(sep, { ...o.style, color: GREY_500 }),
      ])
    ),
    { runSpacing: 3, center: true }
  );
}

/** Contact items stacked vertically (sidebars, right-aligned blocks). */
export function contactColumn(
  c: RenderContext,
  o: { style: TextStyle; iconColor?: Rgb; gap?: number; alignRight?: boolean }
): Node[] {
  const gap = o.gap ?? 5;
  return c.model.contacts.map((item) =>
    padding(
      { bottom: gap },
      o.alignRight
        ? row(
            [
              flexible(linkText(item.text, o.style, item.url, "right")),
              c.showIcons && hspace(5),
              c.showIcons && contactIcon(item.kind, o.iconColor ?? o.style.color, o.style.size * 1.05, c.iconStyle),
            ],
            { justify: "end" }
          )
        : contactChip(c, item, { style: o.style, iconColor: o.iconColor, gap: 6 })
    )
  );
}

/** Text split into page-safe chunks, each its own block. */
export function paragraphBlocks(value: string, style: TextStyle, bottom = 0, align?: TextNode["align"]): Node[] {
  return chunkText(value).map((chunk) => padding({ bottom }, text(chunk, style, align)));
}

/** A bullet row; long bullets continue in extra (glyph-less) rows. */
export function bulletBlocks(
  value: string,
  style: TextStyle,
  o: { glyph: Node; indent?: number; bottom?: number }
): Node[] {
  const indent = o.indent ?? 11;
  return chunkText(value).map((chunk, i) =>
    padding(
      { bottom: o.bottom ?? 2.5 },
      row([sized(indent, undefined, i === 0 ? o.glyph : null), expanded(text(chunk, style))], { align: "start" })
    )
  );
}

/** Small round (or square) bullet aligned to the first text line. */
export function dotGlyph(color: Rgb, fontSize: number, size = 3.2, square = false): Node {
  return padding(
    { top: fontSize * 0.5 - size / 2 + 0.6 },
    box({ width: size, height: size, bg: hex(color), radius: square ? undefined : size / 2 })
  );
}

/** Horizontal level bar that fills its column. */
export function levelBar(fraction: number, o: { color: Rgb; track: Rgb; height?: number }): Node {
  const h = o.height ?? 3.2;
  const filled = Math.min(100, Math.max(1, Math.round(fraction * 100)));
  const rest = Math.min(100, Math.max(1, Math.round((1 - fraction) * 100)));
  return row(
    [
      expanded(box({ height: h, bg: hex(o.color), radius: h / 2 }), filled),
      fraction < 1 && expanded(box({}), rest),
    ],
    { height: h, bg: hex(o.track), radius: h / 2 }
  );
}

/** Five-dot level meter. */
export function levelDots(fraction: number, o: { color: Rgb; track: Rgb; size?: number; gap?: number; count?: number }): Node {
  const size = o.size ?? 5;
  const gap = o.gap ?? 2.5;
  const count = o.count ?? 5;
  const filled = Math.min(count, Math.max(1, Math.round(fraction * count)));
  return rowMin(
    Array.from({ length: count }, (_, i) =>
      box({ width: size, height: size, radius: size / 2, bg: hex(i < filled ? o.color : o.track), margin: [0, i === count - 1 ? 0 : gap, 0, 0] })
    )
  );
}

export function pill(
  value: string,
  style: TextStyle,
  o: { background?: Rgb | null; border?: Rgb | null; radius?: number; hPad?: number; vPad?: number } = {}
): BoxNode {
  const h = o.hPad ?? 6;
  const v = o.vPad ?? 2.5;
  return box(
    {
      pad: [v, h, v, h],
      bg: o.background ? hex(o.background) : undefined,
      border: o.border ? { width: 0.7, color: hex(o.border) } : undefined,
      radius: o.radius ?? 3,
    },
    [text(value, style)]
  );
}

/** The user's photo, or null when absent/disabled. */
export function photoWidget(
  c: RenderContext,
  size: number,
  o: { circle?: boolean; radius?: number; ring?: Rgb; ringWidth?: number } = {}
): Node | null {
  const src = c.model.photo;
  if (!src) return null;
  const circle = o.circle ?? true;
  const radius = o.radius ?? 8;
  const img = image(src, size, circle ? size / 2 : radius);
  if (!o.ring) return img;
  const rw = o.ringWidth ?? 2;
  const outer = size + rw * 2;
  return box({ width: outer, height: outer, pad: [rw, rw, rw, rw], bg: hex(o.ring), radius: circle ? outer / 2 : radius + rw }, [img]);
}

// ---- page frames ------------------------------------------------------------

/** One flowing column with normal margins (left, top, right, bottom). */
export function singleColumnPage(c: RenderContext, margin: Edge, body: Node[]): PageSpec {
  const [left, top, right, bottom] = margin;
  return {
    size: c.format,
    pad: [top, right, bottom, left],
    bands: [],
    footer: { right, bottom: 10, color: hex(c.t.faint), font: c.t.body.regular },
    name: c.model.name,
    body,
  };
}

/** Zero-margin page for full-bleed designs; content pads itself horizontally. */
export function bleedPage(
  c: RenderContext,
  o: {
    top: number;
    bottom: number;
    firstTop?: number;
    bands?: PageSpec["bands"];
    footerRight?: number;
    footerBottom?: number;
    footerColor?: Rgb;
    body?: Node[];
    columns?: Columns;
  }
): PageSpec {
  return {
    size: c.format,
    pad: [o.top, 0, o.bottom, 0],
    firstTop: o.firstTop,
    bands: o.bands ?? [],
    footer: { right: o.footerRight ?? 40, bottom: o.footerBottom ?? 14, color: hex(o.footerColor ?? c.t.faint), font: c.t.body.regular },
    name: c.model.name,
    body: o.body ?? [],
    columns: o.columns,
  };
}

/** Two independently flowing columns (each continues on the next page). */
export function columns(o: { left: Node[]; right: Node[]; leftWidth?: number; rightWidth?: number }): Columns {
  return {
    left: o.left.length ? o.left : [vspace(0)],
    right: o.right.length ? o.right : [vspace(0)],
    leftWidth: o.leftWidth,
    rightWidth: o.rightWidth,
  };
}

export function padHorizontal(child: Node, left: number, right: number): Node {
  return box({ pad: [0, right, 0, left] }, [child]);
}

export function isSpacer(n: Node): boolean {
  return n.kind === "box" && n.children.length === 0 && n.style.height !== undefined && !n.style.bg && !n.style.width;
}

// ---- FlowTemplate ---------------------------------------------------------

export abstract class FlowTemplate {
  abstract readonly info: TemplateInfo;
  abstract readonly fonts: TemplateFonts;
  abstract build(c: RenderContext): PageSpec;
  abstract heading(c: RenderContext, s: RenderSection, z: Zone): Node;

  /**
   * Width of a zone's content column — what the app's LayoutBuilder reports
   * for the skill-level grid. Templates with other margins override it.
   */
  zoneWidth(c: RenderContext, z: Zone): number {
    return z === "main" ? c.format.width - 84 : c.format.width * 0.32 - 42;
  }

  // ---- colours & rhythm ----

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- part of the hook signature templates override
  colors(c: RenderContext, _z: Zone): ZoneColors {
    const t = c.t;
    return { text: t.text, strong: t.ink, muted: t.muted, accent: t.accentText, line: t.line };
  }

  sectionGap(c: RenderContext, z: Zone): number {
    return c.t.gap(z === "main" ? 15 : 13);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- part of the hook signature templates override
  headingGap(c: RenderContext, _z: Zone): number {
    return c.t.gap(7);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- part of the hook signature templates override
  entryGap(c: RenderContext, _z: Zone): number {
    return c.t.gap(9);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- part of the hook signature templates override
  lineGap(c: RenderContext, _z: Zone): number {
    return c.t.gap(2.6);
  }

  // ---- styling hooks ----

  paragraphStyle(c: RenderContext, z: Zone): TextStyle {
    return c.t.style(c.t.body.regular, z === "main" ? 9.3 : 8.6, { color: this.colors(c, z).text });
  }

  bulletStyle(c: RenderContext, z: Zone): TextStyle {
    return this.paragraphStyle(c, z);
  }

  bulletGlyph(c: RenderContext, z: Zone): Node {
    return dotGlyph(this.colors(c, z).accent, this.bulletStyle(c, z).size, 3);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- part of the hook signature templates override
  bulletIndent(_c: RenderContext, _z: Zone): number {
    return 10;
  }

  /** Title / subtitle / dates / meta block of one entry. */
  entryHeader(c: RenderContext, e: RenderEntry, _s: RenderSection, z: Zone): Node {
    const col_ = this.colors(c, z);
    const t = c.t;
    const titleStyle = t.style(t.body.bold, z === "main" ? 10.2 : 9.2, { color: col_.strong });
    const subStyle = t.style(t.body.semiBold, z === "main" ? 9.4 : 8.6, { color: col_.accent });
    const metaStyle = t.style(t.body.regular, 8.3, { color: col_.muted });
    const title = e.title || e.subtitle;
    const subtitle = e.title ? e.subtitle : "";
    return col([
      row(
        [
          expanded(linkText(title, titleStyle, e.url)),
          e.dates && z === "main" && hspace(10),
          e.dates && z === "main" && text(e.dates, metaStyle),
        ],
        { align: "start" }
      ),
      (subtitle || e.meta) &&
        padding(
          { top: 1.5 },
          rich([
            subtitle ? [subtitle, subStyle] : null,
            subtitle && e.meta ? ["  ·  ", metaStyle] : null,
            e.meta ? [e.meta, metaStyle] : null,
          ])
        ),
      e.dates && z === "sidebar" && padding({ top: 1.5 }, text(e.dates, metaStyle)),
    ]);
  }

  /** Technologies under a project. */
  techTags(c: RenderContext, tags: string[], z: Zone): Node {
    const col_ = this.colors(c, z);
    return wrapBox(
      tags.map((tag) =>
        pill(tag, c.t.style(c.t.body.regular, 7.6, { color: col_.text, lineSpacing: 0 }), {
          background: z === "main" ? c.t.accentTint : null,
          border: z === "main" ? null : col_.line,
        })
      ),
      { spacing: 4, runSpacing: 3 }
    );
  }

  // ---- list-like sections ----

  skillsContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    const col_ = this.colors(c, z);
    const t = c.t;
    if (c.showSkillLevels) return this.levelList(c, s.skills, z, { grouped: hasCategories(s) });
    if (hasCategories(s)) {
      return skillGroups(s).map(([key, list]) =>
        padding(
          { bottom: this.lineGap(c, z) + 1 },
          rich([
            key ? [`${key}: `, t.style(t.body.bold, 8.9, { color: col_.strong })] : null,
            [list.map((x) => x.name).join(", "), t.style(t.body.regular, 8.9, { color: col_.text })],
          ])
        )
      );
    }
    return [this.chips(c, s.skills.map((x) => x.name), z)];
  }

  /**
   * Skill/language names with bars (or dots), optionally under category
   * labels. In the main column the cells form a 2–3 column grid sized to the
   * column's width; in a sidebar they stack.
   */
  levelList(c: RenderContext, items: RenderSkill[], z: Zone, o: { grouped?: boolean; dots?: boolean } = {}): Node[] {
    const col_ = this.colors(c, z);
    const t = c.t;
    const nameStyle = t.style(t.body.regular, z === "main" ? 8.8 : 8.4, { color: col_.text, lineSpacing: 0 });
    const track = z === "sidebar" && Pal.isDark(col_.line) ? col_.line : Pal.tint(col_.accent, 0.82);
    const cell = (s: RenderSkill): Node =>
      padding(
        { bottom: t.gap(5) },
        o.dots
          ? row([expanded(text(s.name, nameStyle)), levelDots(s.level, { color: col_.accent, track, size: 4.6 })])
          : col([text(s.name, nameStyle), vspace(2.5), levelBar(s.level, { color: col_.accent, track, height: 3 })])
      );
    const grid = (list: RenderSkill[]): Node => {
      const width = this.zoneWidth(c, z);
      const cols = width > 380 ? 3 : 2;
      const gap = 18;
      // Floored so rounding can never push the last cell of a row onto the next.
      const cellWidth = Math.floor(((width - gap * (cols - 1)) / cols) * 100) / 100;
      return wrapBox(list.map((s) => sized(cellWidth, undefined, cell(s))), { spacing: gap, cross: "start" });
    };
    const block = (list: RenderSkill[]): Node => (z === "main" ? grid(list) : keep(list.map(cell)));

    if (!o.grouped) return z === "main" ? [grid(items)] : items.map(cell);
    const groups = new Map<string, RenderSkill[]>();
    for (const s of items) {
      const list = groups.get(s.category) ?? [];
      list.push(s);
      groups.set(s.category, list);
    }
    // Each category label travels with its own items.
    return [...groups.entries()].map(([key, list]) =>
      keep([
        key && padding({ top: 1, bottom: 3 }, text(key, t.style(t.body.semiBold, 8.2, { color: col_.muted, lineSpacing: 0 }))),
        block(list),
      ])
    );
  }

  /** Inline chips for skills/interests. */
  chips(c: RenderContext, items: string[], z: Zone): Node {
    const col_ = this.colors(c, z);
    return wrapBox(
      items.map((item) =>
        pill(item, c.t.style(c.t.body.regular, 8.3, { color: col_.strong, lineSpacing: 0 }), {
          background: z === "main" ? c.t.accentTint : null,
          border: z === "main" ? null : col_.line,
          hPad: 6.5,
          vPad: 2.8,
        })
      ),
      { spacing: 4.5, runSpacing: 4 }
    );
  }

  languagesContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    if (c.showSkillLevels) return this.levelList(c, s.skills, z, { dots: true });
    const col_ = this.colors(c, z);
    const t = c.t;
    return s.skills.map((l) =>
      padding(
        { bottom: this.lineGap(c, z) + 0.5 },
        rich([
          [l.name, t.style(t.body.semiBold, 8.9, { color: col_.strong })],
          [`  ${l.levelLabel}`, t.style(t.body.regular, 8.4, { color: col_.muted })],
        ])
      )
    );
  }

  tagsContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    return [this.chips(c, s.tags, z)];
  }

  referencesContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    const col_ = this.colors(c, z);
    const t = c.t;
    if (!s.entries.length) {
      return [text("Available upon request.", t.style(t.body.italic, 9, { color: col_.muted }))];
    }
    return s.entries.map((e) =>
      padding(
        { bottom: this.entryGap(c, z) * 0.8 },
        col([
          e.title && text(e.title, t.style(t.body.bold, 9.3, { color: col_.strong })),
          e.subtitle && text(e.subtitle, t.style(t.body.regular, 8.6, { color: col_.text })),
          e.meta && text(e.meta, t.style(t.body.regular, 8.3, { color: col_.muted })),
        ])
      )
    );
  }

  certificationsContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    if (z === "main") return s.entries.flatMap((e) => this.entryBlocks(c, e, s, z));
    const col_ = this.colors(c, z);
    const t = c.t;
    return s.entries.map((e) =>
      padding(
        { bottom: t.gap(6) },
        col([
          linkText(e.title, t.style(t.body.semiBold, 8.8, { color: col_.strong }), e.url),
          (e.subtitle || e.dates) &&
            text(
              [e.subtitle, e.dates].filter(Boolean).join(" · "),
              t.style(t.body.regular, 8.1, { color: col_.muted })
            ),
        ])
      )
    );
  }

  // ---- assembly ----

  /** Header (kept with its first line), then each line, then tags. */
  entryBlocks(c: RenderContext, e: RenderEntry, s: RenderSection, z: Zone): Node[] {
    const lines: Node[] = [];
    for (const l of e.lines) {
      if (l.bullet) {
        lines.push(
          ...bulletBlocks(l.text, this.bulletStyle(c, z), {
            glyph: this.bulletGlyph(c, z),
            indent: this.bulletIndent(c, z),
            bottom: this.lineGap(c, z),
          })
        );
      } else {
        lines.push(...paragraphBlocks(l.text, this.paragraphStyle(c, z), this.lineGap(c, z) + 0.5));
      }
    }
    if (e.tags.length) lines.push(padding({ top: 2 }, this.techTags(c, e.tags, z)));
    const header = entryHasHeader(e)
      ? padding({ bottom: lines.length ? c.t.gap(3.5) : 0 }, this.entryHeader(c, e, s, z))
      : null;
    const blocks: Node[] = [];
    if (header) blocks.push(keep([header, lines[0]]));
    else if (lines.length) blocks.push(lines[0]);
    blocks.push(...lines.slice(1));
    if (!blocks.length) return [];
    blocks.push(vspace(this.entryGap(c, z)));
    return blocks;
  }

  /** Content blocks for [s] (without its heading). */
  contentBlocks(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    switch (s.type) {
      case "summary":
        return s.lines.flatMap((l) =>
          l.bullet
            ? bulletBlocks(l.text, this.bulletStyle(c, z), {
                glyph: this.bulletGlyph(c, z),
                indent: this.bulletIndent(c, z),
                bottom: this.lineGap(c, z),
              })
            : paragraphBlocks(l.text, this.paragraphStyle(c, z), this.lineGap(c, z) + 2)
        );
      case "bullets":
        return s.lines.flatMap((l) =>
          bulletBlocks(l.text, this.bulletStyle(c, z), {
            glyph: this.bulletGlyph(c, z),
            indent: this.bulletIndent(c, z),
            bottom: this.lineGap(c, z) + 0.5,
          })
        );
      case "skills":
        return this.skillsContent(c, s, z);
      case "languages":
        return this.languagesContent(c, s, z);
      case "tags":
        return this.tagsContent(c, s, z);
      case "references":
        return this.referencesContent(c, s, z);
      case "certifications":
        return this.certificationsContent(c, s, z);
      default: {
        const out = s.entries.flatMap((e) => this.entryBlocks(c, e, s, z));
        // Drop the trailing entry gap; the section gap follows.
        if (out.length && isSpacer(out[out.length - 1])) out.pop();
        return out;
      }
    }
  }

  /** Heading + content; the heading is kept with the first content block. */
  sectionBlocks(c: RenderContext, s: RenderSection, z: Zone = "main"): Node[] {
    const content = this.contentBlocks(c, s, z);
    return [
      keep([this.heading(c, s, z), vspace(this.headingGap(c, z)), content[0]]),
      ...content.slice(1),
      vspace(this.sectionGap(c, z)),
    ];
  }

  allSections(c: RenderContext, sections: RenderSection[], z: Zone = "main"): Node[] {
    return sections.flatMap((s) => this.sectionBlocks(c, s, z).map((b) => tagged(b, s.key)));
  }
}

/** [n] marked as drawn by the resume part [tag] (glyphs and images can't be tagged). */
export function tagged(n: Node, tag: string): Node {
  return n.kind === "box" || n.kind === "text" ? { ...n, tag } : n;
}

function hasTag(n: Node): boolean {
  if (n.kind !== "box" && n.kind !== "text") return false;
  if (n.tag) return true;
  return n.kind === "box" && n.children.length === 1 && hasTag(n.children[0]);
}

/** Marks every top-level block no section claimed (header, contact panel, photo) as [tag]. */
export function tagRest(nodes: Node[], tag: string): Node[] {
  return nodes.map((n) => (hasTag(n) ? n : tagged(n, tag)));
}
